import {
  Component,
  NgZone,
  OnInit,
  Signal,
  ViewChild,
  ViewChildren,
  QueryList,
  computed,
  effect,
  inject,
  input,
  ElementRef,
  AfterViewInit,
  TemplateRef,
} from '@angular/core';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';

import { SidebarService } from './data-access/sidebar.service';
import {
  BreakpointObserver,
  BreakpointState,
  Breakpoints,
} from '@angular/cdk/layout';
import { Observable, combineLatest, map, tap } from 'rxjs';
import { HeaderComponent } from './features/header/header.component';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { TooltipDirective } from '../../shared/directives/tooltip/tooltip.directive';
import { RoutesExtended, SidebarItem, SidebarComponentItem } from './sidebar-types';
import { SidebarItemComponent } from './components/sidebar-page/sidebar-page.component';
import { PropertyService } from '../../../services/property.service';

@Component({
    selector: 'sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.css'],
    imports: [
        RouterModule,
        HeaderComponent,
        CommonModule,
        TooltipDirective,
        SidebarItemComponent,
    ],
    host: {
      class: 'absolute right-0 top-0 bottom-0 z-[99999]'
    }
})
export class SidebarComponent implements OnInit, AfterViewInit {
  @ViewChild('sideNav', { static: true }) drawer: ElementRef;
  @ViewChildren(SidebarItemComponent) sidebarItems!: QueryList<SidebarItemComponent>;

  protected readonly breakpointObserver: BreakpointObserver =
    inject(BreakpointObserver);

  isSmallScreen$: Observable<boolean>;

  isSmallScreen: Signal<boolean>;
  display;
  activeProperty = toSignal(this.propertyService.activeProperty);

  constructor(
    private zone: NgZone,
    private router: Router,
    public route: ActivatedRoute,
    public sidebarService: SidebarService,
    private propertyService: PropertyService
  ) {
    this.isSmallScreen$ = this.breakpointObserver
      .observe([Breakpoints.Small, Breakpoints.XSmall])
      .pipe(map((state: BreakpointState) => state.matches));

    this.isSmallScreen = toSignal(this.isSmallScreen$, { initialValue: false });

    this.display = computed(() => {
      return this.isSmallScreen() ? 'none' : 'grid';
    });
  }

  ngOnInit(): void {
    this.sidebarService.setDrawer(this.drawer);
  }

  ngAfterViewInit(): void {
    // The sidebar items are now self-contained and handle their own registration
  }

  // Handle clicks on sidebar items from the service pages
  handlePageClick(page: SidebarItem): void {
    if (this.isComponentItem(page)) {
      this.sidebarService.toggleComponentItem(page.id);
    } else {
      // For route-based items, navigate to the route
      if (page.route) {
        this.router.navigate([page.route], { relativeTo: this.route });
      }
    }
  }

  // Handle clicks on sidebar items directly
  handleSidebarItemClick(item: SidebarItemComponent): void {
    item.handleClick();
  }

  // Check if there are any active component items
  hasActiveComponentItem(): boolean {
    return this.sidebarService.pages().some(item =>
      this.isComponentItem(item) && item.active
    );
  }

  // Get all active component items
  getActiveComponentItems(): SidebarComponentItem[] {
    return this.sidebarService.pages()
      .filter(item => this.isComponentItem(item) && item.active)
      .map(item => item as SidebarComponentItem);
  }

  // Get sidebar items for rendering
  getSidebarItems(): SidebarItemComponent[] {
    return this.sidebarItems?.toArray() || [];
  }

  private isComponentItem(page: SidebarItem): page is SidebarComponentItem {
    return 'id' in page && 'contentTemplate' in page;
  }

  isActivePropertySelected(): boolean {
    return this.activeProperty() !== null;
  }

  getSidebarIconTemplate(id: string): TemplateRef<any> | null {
    return this.sidebarService.getSidebarIconTemplate(id);
  }
}
